/**
 * Mock implementation of Telegram WebApp API for development
 * This allows running the app in browser without Telegram
 */

export interface MockTelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

export interface MockInitDataUnsafe {
  user?: MockTelegramUser;
  auth_date?: number;
  hash?: string;
  query_id?: string;
  start_param?: string;
}

class MockTelegramWebApp {
  version = "6.9";
  platform = "web";
  
  private getSystemColorScheme(): "light" | "dark" {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  
  colorScheme: "light" | "dark" = this.getSystemColorScheme();
  
  private getLightThemeParams() {
    return {
      accent_text_color: "#168acd",
      bg_color: "#ffffff",
      bottom_bar_bg_color: "#ffffff",
      button_color: "#40a7e3",
      button_text_color: "#ffffff",
      destructive_text_color: "#d14e4e",
      header_bg_color: "#ffffff",
      hint_color: "#999999",
      link_color: "#168acd",
      secondary_bg_color: "#f1f1f1",
      section_bg_color: "#ffffff",
      section_header_text_color: "#168acd",
      section_separator_color: "#e7e7e7",
      subtitle_text_color: "#999999",
      text_color: "#000000",
    };
  }
  
  private getDarkThemeParams() {
    return {
      accent_text_color: "#64d5fa",
      bg_color: "#1a2c33",
      bottom_bar_bg_color: "#1a2c33",
      button_color: "#58bfe8",
      button_text_color: "#ffffff",
      destructive_text_color: "#ec3942",
      header_bg_color: "#1a2c33",
      hint_color: "#83a8b7",
      link_color: "#63d4fa",
      secondary_bg_color: "#283d48",
      section_bg_color: "#1a2c33",
      section_header_text_color: "#63d4fa",
      section_separator_color: "#132127",
      subtitle_text_color: "#83a8b7",
      text_color: "#f5f5f5",
    };
  }
  
  get themeParams() {
    return this.colorScheme === "dark" ? this.getDarkThemeParams() : this.getLightThemeParams();
  }
  
  initData = "";
  initDataUnsafe: MockInitDataUnsafe = {
    user: {
      id: 123456789,
      first_name: "Dev",
      last_name: "User",
      // username is intentionally omitted to test userId display
      language_code: "en",
      is_premium: false,
    },
    auth_date: Math.floor(Date.now() / 1000),
    hash: "mock_hash",
  };
  
  isExpanded = true;
  viewportHeight = window.innerHeight;
  viewportStableHeight = window.innerHeight;
  headerColor = "#ffffff";
  backgroundColor = "#ffffff";
  isClosingConfirmationEnabled = false;
  
  private backButtonCallback: (() => void) | null = null;
  
  BackButton = {
    isVisible: false,
    show: () => {
      this.BackButton.isVisible = true;
      console.log("[MockTelegram] BackButton.show()");
      // In dev mode, you can trigger back button by pressing Escape key
      if (typeof window !== 'undefined') {
        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === 'Escape' && this.BackButton.isVisible && this.backButtonCallback) {
            e.preventDefault();
            this.backButtonCallback();
          }
        };
        window.addEventListener('keydown', handleEscape);
        // Store cleanup function
        (this.BackButton as any)._cleanup = () => {
          window.removeEventListener('keydown', handleEscape);
        };
      }
    },
    hide: () => {
      this.BackButton.isVisible = false;
      console.log("[MockTelegram] BackButton.hide()");
      // Cleanup event listener
      if ((this.BackButton as any)._cleanup) {
        (this.BackButton as any)._cleanup();
        (this.BackButton as any)._cleanup = null;
      }
    },
    onClick: (callback: () => void) => {
      this.backButtonCallback = callback;
      console.log("[MockTelegram] BackButton.onClick() registered");
      return () => {
        this.backButtonCallback = null;
      };
    },
    offClick: () => {
      this.backButtonCallback = null;
    },
  };
  
  MainButton = {
    text: "",
    color: "",
    textColor: "",
    isVisible: false,
    isActive: true,
    isProgressVisible: false,
    setText: (text: string) => {
      this.MainButton.text = text;
      console.log("[MockTelegram] MainButton.setText()", text);
    },
    onClick: (callback: () => void) => {
      console.log("[MockTelegram] MainButton.onClick() registered");
      return () => {};
    },
    offClick: () => {},
    show: () => {
      this.MainButton.isVisible = true;
      console.log("[MockTelegram] MainButton.show()");
    },
    hide: () => {
      this.MainButton.isVisible = false;
      console.log("[MockTelegram] MainButton.hide()");
    },
    enable: () => {
      this.MainButton.isActive = true;
      console.log("[MockTelegram] MainButton.enable()");
    },
    disable: () => {
      this.MainButton.isActive = false;
      console.log("[MockTelegram] MainButton.disable()");
    },
    showProgress: (leaveActive?: boolean) => {
      this.MainButton.isProgressVisible = true;
      console.log("[MockTelegram] MainButton.showProgress()", leaveActive);
    },
    hideProgress: () => {
      this.MainButton.isProgressVisible = false;
      console.log("[MockTelegram] MainButton.hideProgress()");
    },
    setParams: (params: {
      text?: string;
      color?: string;
      text_color?: string;
      is_active?: boolean;
      is_visible?: boolean;
    }) => {
      if (params.text) this.MainButton.text = params.text;
      if (params.color) this.MainButton.color = params.color;
      if (params.text_color) this.MainButton.textColor = params.text_color;
      if (params.is_active !== undefined) this.MainButton.isActive = params.is_active;
      if (params.is_visible !== undefined) this.MainButton.isVisible = params.is_visible;
      console.log("[MockTelegram] MainButton.setParams()", params);
    },
  };
  
  BottomButton = this.MainButton;
  
  SecondaryButton = {
    text: "",
    color: "",
    textColor: "",
    isVisible: false,
    isActive: true,
    isProgressVisible: false,
    setText: (text: string) => {
      this.SecondaryButton.text = text;
      console.log("[MockTelegram] SecondaryButton.setText()", text);
    },
    onClick: (callback: () => void) => {
      console.log("[MockTelegram] SecondaryButton.onClick() registered");
      return () => {};
    },
    offClick: () => {},
    show: () => {
      this.SecondaryButton.isVisible = true;
      console.log("[MockTelegram] SecondaryButton.show()");
    },
    hide: () => {
      this.SecondaryButton.isVisible = false;
      console.log("[MockTelegram] SecondaryButton.hide()");
    },
    showProgress: (leaveActive?: boolean) => {
      this.SecondaryButton.isProgressVisible = true;
      console.log("[MockTelegram] SecondaryButton.showProgress()", leaveActive);
    },
    hideProgress: () => {
      this.SecondaryButton.isProgressVisible = false;
      console.log("[MockTelegram] SecondaryButton.hideProgress()");
    },
    setParams: (params: {
      text?: string;
      color?: string;
      text_color?: string;
      is_active?: boolean;
      is_visible?: boolean;
    }) => {
      if (params.text) this.SecondaryButton.text = params.text;
      if (params.color) this.SecondaryButton.color = params.color;
      if (params.text_color) this.SecondaryButton.textColor = params.text_color;
      if (params.is_active !== undefined) this.SecondaryButton.isActive = params.is_active;
      if (params.is_visible !== undefined) this.SecondaryButton.isVisible = params.is_visible;
      console.log("[MockTelegram] SecondaryButton.setParams()", params);
    },
  };
  
  HapticFeedback = {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft" = "medium") => {
      console.log("[MockTelegram] HapticFeedback.impactOccurred()", style);
    },
    notificationOccurred: (type: "error" | "success" | "warning") => {
      console.log("[MockTelegram] HapticFeedback.notificationOccurred()", type);
    },
    selectionChanged: () => {
      console.log("[MockTelegram] HapticFeedback.selectionChanged()");
    },
  };
  
  CloudStorage = null;
  DeviceStorage = null;
  SecureStorage = null;
  
  ready = () => {
    console.log("[MockTelegram] WebApp.ready()");
  };
  
  expand = () => {
    this.isExpanded = true;
    console.log("[MockTelegram] WebApp.expand()");
  };
  
  close = () => {
    console.log("[MockTelegram] WebApp.close()");
    console.warn("[MockTelegram] close() called - this is a mock, nothing happens");
  };
  
  enableClosingConfirmation = () => {
    this.isClosingConfirmationEnabled = true;
    console.log("[MockTelegram] WebApp.enableClosingConfirmation()");
  };
  
  disableClosingConfirmation = () => {
    this.isClosingConfirmationEnabled = false;
    console.log("[MockTelegram] WebApp.disableClosingConfirmation()");
  };
  
  setHeaderColor = (color: string) => {
    this.headerColor = color;
    console.log("[MockTelegram] WebApp.setHeaderColor()", color);
  };
  
  setBackgroundColor = (color: string) => {
    this.backgroundColor = color;
    console.log("[MockTelegram] WebApp.setBackgroundColor()", color);
  };
  
  onEvent = (eventType: string, eventHandler: () => void) => {
    console.log("[MockTelegram] WebApp.onEvent()", eventType);
    return () => {};
  };
  
  offEvent = (eventType: string, eventHandler: () => void) => {
    console.log("[MockTelegram] WebApp.offEvent()", eventType);
  };
  
  sendData = (data: string) => {
    console.log("[MockTelegram] WebApp.sendData()", data);
  };
  
  openLink = (url: string, options?: { try_instant_view?: boolean }) => {
    console.log("[MockTelegram] WebApp.openLink()", url, options);
    if (typeof window !== "undefined") {
      window.open(url, "_blank");
    }
  };
  
  openTelegramLink = (url: string) => {
    console.log("[MockTelegram] WebApp.openTelegramLink()", url);
    this.openLink(url);
  };
  
  showPopup = (params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: "default" | "ok" | "close" | "cancel" | "destructive"; text: string }> }, cb?: (buttonId?: string) => void) => {
    console.log("[MockTelegram] WebApp.showPopup()", params);
    if (typeof window !== "undefined" && window.confirm) {
      const confirmed = window.confirm(params.message);
      if (cb) {
        cb(confirmed ? "ok" : "cancel");
      }
    }
  };
  
  showAlert = (message: string, cb?: () => void) => {
    console.log("[MockTelegram] WebApp.showAlert()", message);
    if (typeof window !== "undefined" && window.alert) {
      window.alert(message);
      if (cb) cb();
    }
  };
  
  showConfirm = (messageOrParams: string | { message: string; title?: string }, cb?: (ok: boolean) => void) => {
    const message = typeof messageOrParams === "string" ? messageOrParams : messageOrParams.message;
    console.log("[MockTelegram] WebApp.showConfirm()", message);
    if (typeof window !== "undefined" && window.confirm) {
      const confirmed = window.confirm(message);
      if (cb) cb(confirmed);
    }
  };
  
  switchInlineQuery = (query: string, choose_chat_types?: ("users" | "bots" | "groups" | "channels")[]) => {
    console.log("[MockTelegram] WebApp.switchInlineQuery()", query, choose_chat_types);
  };
  
  // Additional optional methods
  requestFullscreen = async () => {
    console.log("[MockTelegram] WebApp.requestFullscreen()");
  };
  
  exitFullscreen = async () => {
    console.log("[MockTelegram] WebApp.exitFullscreen()");
  };
  
  lockOrientation = async (orientation: "portrait" | "landscape") => {
    console.log("[MockTelegram] WebApp.lockOrientation()", orientation);
  };
  
  unlockOrientation = async () => {
    console.log("[MockTelegram] WebApp.unlockOrientation()");
  };
  
  setBottomBarColor = (color: string) => {
    console.log("[MockTelegram] WebApp.setBottomBarColor()", color);
  };
  
  bottomBarColor = "#ffffff";
  
  isFullscreen = false;
  isActive = true;
  safeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };
  contentSafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };
  isVerticalSwipesEnabled = false;
  
  SettingsButton = undefined;
  
  Accelerometer = undefined;
  Gyroscope = undefined;
  DeviceOrientation = undefined;
  LocationManager = undefined;
  BiometricManager = undefined;
  isOrientationLocked = false;
  
  // Generate mock initData string
  private generateMockInitData(): string {
    const user = this.initDataUnsafe.user;
    if (!user) return "";
    
    const params = new URLSearchParams();
    params.set("user", JSON.stringify({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      username: user.username,
      language_code: user.language_code,
      is_premium: user.is_premium,
    }));
    params.set("auth_date", String(this.initDataUnsafe.auth_date || Math.floor(Date.now() / 1000)));
    params.set("hash", this.initDataUnsafe.hash || "mock_hash");
    
    return params.toString();
  }
  
  constructor() {
    // Generate initData on construction
    this.initData = this.generateMockInitData();
    
    // Update viewport height on resize
    if (typeof window !== "undefined") {
      window.addEventListener("resize", () => {
        this.viewportHeight = window.innerHeight;
        this.viewportStableHeight = window.innerHeight;
      });
      
      // Listen for system theme changes
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleThemeChange = (e: MediaQueryListEvent) => {
        this.colorScheme = e.matches ? "dark" : "light";
        // Trigger themeChanged event
        if (this.onEvent) {
          this.onEvent("themeChanged", () => {});
        }
        console.log("[MockTelegram] Theme changed to:", this.colorScheme);
      };
      mediaQuery.addEventListener("change", handleThemeChange);
    }
    
    console.log("[MockTelegram] Initialized mock Telegram WebApp");
    console.log("[MockTelegram] User:", this.initDataUnsafe.user);
    console.log("[MockTelegram] Theme:", this.colorScheme);
  }
}

// Extend Window interface to include mock Telegram WebApp
// Note: Telegram namespace is declared in src/types/telegram.d.ts
// We use type assertion in telegram.ts to handle both real and mock types

/**
 * Initialize mock Telegram WebApp
 * Call this in dev mode before the app tries to access Telegram API
 */
export function initializeMockTelegramWebApp(user?: Partial<MockTelegramUser>): void {
  if (typeof window === "undefined") return;
  
  const mockWebApp = new MockTelegramWebApp();
  
  if (user) {
    mockWebApp.initDataUnsafe.user = {
      ...mockWebApp.initDataUnsafe.user!,
      ...user,
    };
    mockWebApp.initData = mockWebApp["generateMockInitData"]();
  }
  
  window.Telegram = {
    WebApp: mockWebApp as any,
  };
  
  console.log("[MockTelegram] Mock Telegram WebApp initialized");
}

